import { inject, Injectable } from '@angular/core';
import { RecaptchaService } from '@principle-theorem/ng-shared';
import {
  type IPatientFormSchema,
  type IPatientMedicalHistoryFormTokenData,
  type ISubmitMedicalFormRequest,
  type ITokenRequest,
  PATIENT_PORTAL_DISPATCHER,
  PatientAction,
  PatientActionRequest,
} from '@principle-theorem/principle-core/interfaces';
import {
  httpsCallable,
  serialise,
  type SerialisedData,
  snapshot,
  unserialise,
} from '@principle-theorem/shared';

@Injectable()
export class PatientMedicalHistoryService {
  private _recaptcha = inject(RecaptchaService);

  async resolvePatientMedicalHistoryData$(
    tokenUid: string
  ): Promise<IPatientMedicalHistoryFormTokenData | undefined> {
    try {
      const payload: PatientActionRequest<ITokenRequest> = {
        action: PatientAction.GetPatientMedicalHistoryFormData,
        data: { tokenUid },
        recaptchaToken: await this._recaptcha.execute(
          PatientAction.GetPatientMedicalHistoryFormData
        ),
      };

      const request = await snapshot(
        httpsCallable<
          PatientActionRequest<ITokenRequest>,
          SerialisedData<IPatientMedicalHistoryFormTokenData>
        >(PATIENT_PORTAL_DISPATCHER)(payload)
      );

      return unserialise(request);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error resolving patient medical history data:', e);
      return undefined;
    }
  }

  async submitFormData(
    tokenUid: string,
    patientFormSchema: IPatientFormSchema
  ): Promise<void> {
    const data: SerialisedData<ISubmitMedicalFormRequest> = serialise({
      tokenUid,
      data: patientFormSchema,
    });
    const payload = {
      action: PatientAction.SubmitPatientMedicalForm,
      data,
      recaptchaToken: await this._recaptcha.execute(
        PatientAction.SubmitPatientMedicalForm
      ),
    };

    await snapshot(
      httpsCallable<
        PatientActionRequest<SerialisedData<ISubmitMedicalFormRequest>>
      >(PATIENT_PORTAL_DISPATCHER)(payload)
    );
  }
}
